import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Card, DatePicker, Select, Space, Typography, Table, Tag, Row, Col, Statistic, Switch, Tooltip, Button, message, Alert, Divider, Segmented, Skeleton, Input } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs, { Dayjs } from 'dayjs';
import { ReloadOutlined, InfoCircleOutlined } from '@ant-design/icons';
import {
  usageService,
  type UsageResponse,
  type DailyUsageRecord,
  type ClientSummaryRecord,
  type UsageTotals,
  type DailyTotalRecord,
} from '../services/usage';
import { statisticsService, type ServiceUsageStats } from '../services/statistics';

const { RangePicker } = DatePicker;
const { Title, Paragraph, Text } = Typography;

type ViewMode = 'summary' | 'daily';

interface ClientSummaryWithRank extends ClientSummaryRecord {
  key: string;
  rank: number;
}

interface DailyUsageWithKey extends DailyUsageRecord {
  key: string;
}

interface DailyTotalWithKey extends DailyTotalRecord {
  key: string;
}

const createDefaultRange = (): [Dayjs, Dayjs] => [dayjs(), dayjs()];

const numberFormatter = (value: number | string): string => {
  const num = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(num)) return '0';
  return num.toLocaleString();
};

const percentFormatter = (value: number): string => `${value.toFixed(2)}%`;

const UsageManagement: React.FC = () => {
  const [dateRange, setDateRange] = useState<[Dayjs, Dayjs]>(createDefaultRange());
  const [selectedClients, setSelectedClients] = useState<number[]>([]);
  const [clientIdInput, setClientIdInput] = useState<string>(''); // 客户ID输入
  const [includeEmpty, setIncludeEmpty] = useState<boolean>(true);
  const [viewMode, setViewMode] = useState<ViewMode>('summary');
  const [loading, setLoading] = useState<boolean>(false);
  const [usageData, setUsageData] = useState<UsageResponse | null>(null);
  const [serviceUsage, setServiceUsage] = useState<ServiceUsageStats | null>(null);

  const loadUsageData = useCallback(async () => {
    if (!dateRange[0] || !dateRange[1]) return;

    try {
      setLoading(true);
      const [payload, serviceStats] = await Promise.all([
        usageService.getDailyUsage({
          startDate: dateRange[0].format('YYYY-MM-DD'),
          endDate: dateRange[1].format('YYYY-MM-DD'),
          clientIds: selectedClients.length > 0 ? selectedClients : undefined,
          includeEmpty,
        }),
        statisticsService.getServiceUsageStats({
          client_id: selectedClients.length === 1 ? selectedClients[0] : undefined,
          start_date: dateRange[0].format('YYYY-MM-DD'),
          end_date: dateRange[1].format('YYYY-MM-DD'),
        }).catch((err) => {
          console.error('获取服务用量统计失败:', err);
          return null;
        }), // 服务用量统计失败不影响主数据
      ]);
      setUsageData(payload);
      if (serviceStats) {
        setServiceUsage(serviceStats);
      } else {
        // 如果服务用量为空，设置默认值显示 0
        setServiceUsage({
          summary: {
            total_requests: 0,
            ipcore_usage: 0,
            fingerprint_usage: 0,
            start_date: dateRange[0].format('YYYY-MM-DD'),
            end_date: dateRange[1].format('YYYY-MM-DD'),
          },
          daily: [],
        });
      }
    } catch (error) {
      console.error('加载用量数据失败', error);
      message.error('获取用量数据失败，请稍后重试');
    } finally {
      setLoading(false);
    }
  }, [dateRange, selectedClients, includeEmpty]);

  useEffect(() => {
    loadUsageData();
  }, [loadUsageData]);

  const handleRangeChange = (values: null | [Dayjs | null, Dayjs | null]) => {
    if (!values || !values[0] || !values[1]) {
      setDateRange(createDefaultRange());
      return;
    }
    setDateRange([values[0], values[1]]);
  };

  const handleRefresh = () => {
    loadUsageData();
  };

  const clientOptions = useMemo(() => {
    if (!usageData) return [];
    return usageData.clientMeta.map((client) => ({
      label: `${client.username} (${client.email || '未填写邮箱'})`,
      value: client.id,
    }));
  }, [usageData]);

  const filteredSummary: ClientSummaryWithRank[] = useMemo(() => {
    if (!usageData) return [];
    const baseList = usageData.clientSummary.filter((item) => {
      if (selectedClients.length > 0 && !selectedClients.includes(item.clientId)) {
        return false;
      }
      if (!includeEmpty && item.totalVisits === 0) {
        return false;
      }
      return true;
    });

    return baseList.map((item, index) => ({
      ...item,
      key: `${item.clientId}`,
      rank: index + 1,
    }));
  }, [usageData, selectedClients, includeEmpty]);

  const filteredDailyUsage: DailyUsageWithKey[] = useMemo(() => {
    if (!usageData) return [];
    return usageData.dailyUsage
      .filter((item) => {
        if (selectedClients.length > 0 && !selectedClients.includes(item.clientId)) {
          return false;
        }
        if (!includeEmpty && item.totalVisits === 0) {
          return false;
        }
        return true;
      })
      .map((item, index) => ({
        ...item,
        key: `${item.date}-${item.clientId}-${index}`,
      }));
  }, [usageData, selectedClients, includeEmpty]);

  const dailyTotalsWithKeys: DailyTotalWithKey[] = useMemo(() => {
    if (!usageData) return [];
    return usageData.dailyTotals.map((item, index) => ({
      ...item,
      key: `${item.date}-${index}`,
    }));
  }, [usageData]);

  const totals: UsageTotals | null = usageData?.totals ?? null;

  const summaryColumns: ColumnsType<ClientSummaryWithRank> = [
    {
      title: '排名',
      dataIndex: 'rank',
      width: 80,
      align: 'center',
      render: (rank) => <Tag color={rank <= 3 ? 'cyan' : 'blue'}>{rank}</Tag>,
    },
    {
      title: '客户ID',
      dataIndex: 'clientId',
      width: 80,
      align: 'center',
      render: (id: number) => <Text code>{id}</Text>,
    },
    {
      title: '客户账号',
      dataIndex: 'clientName',
      render: (text, record) => (
        <Space direction="vertical" size={0}>
          <Text strong>{text}</Text>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {record.email || '未设置邮箱'}
          </Text>
        </Space>
      ),
    },
    {
      title: '总访问量',
      dataIndex: 'totalVisits',
      sorter: (a, b) => a.totalVisits - b.totalVisits,
      defaultSortOrder: 'descend',
      render: numberFormatter,
    },
    {
      title: '计费事件',
      dataIndex: 'billableEvents',
      render: (value) => <Text>{numberFormatter(value)}</Text>,
    },
    {
      title: '正常访问',
      dataIndex: 'normalVisits',
      render: numberFormatter,
    },
    {
      title: '拦截访问',
      dataIndex: 'interceptedVisits',
      render: (value) => (
        <Tag color={value > 0 ? 'volcano' : 'default'}>{numberFormatter(value)}</Tag>
      ),
    },
    {
      title: '活跃天数',
      dataIndex: 'activeDays',
      align: 'center',
    },
    {
      title: '平均日访问',
      dataIndex: 'averageDailyVisits',
      render: (value) => value.toFixed(2),
    },
    {
      title: '拦截率',
      dataIndex: 'interceptRate',
      render: (value) => (
        <Tag color={value > 30 ? 'red' : value > 10 ? 'orange' : 'green'}>
          {percentFormatter(value)}
        </Tag>
      ),
    },
    {
      title: 'IPCore',
      dataIndex: 'ipcoreUsage',
      render: (value) => <Tag color="blue">{numberFormatter(value || 0)}</Tag>,
    },
    {
      title: '指纹识别',
      dataIndex: 'fingerprintUsage',
      render: (value) => <Tag color="purple">{numberFormatter(value || 0)}</Tag>,
    },
  ];

  const dailyColumns: ColumnsType<DailyUsageWithKey> = [
    {
      title: '日期',
      dataIndex: 'date',
      sorter: (a, b) => a.date.localeCompare(b.date),
    },
    {
      title: '客户ID',
      dataIndex: 'clientId',
      width: 80,
      align: 'center',
      render: (id: number) => <Text code>{id}</Text>,
    },
    {
      title: '客户账号',
      dataIndex: 'clientName',
      sorter: (a, b) => a.clientName.localeCompare(b.clientName),
    },
    {
      title: '总访问量',
      dataIndex: 'totalVisits',
      render: numberFormatter,
      sorter: (a, b) => a.totalVisits - b.totalVisits,
    },
    {
      title: '计费事件',
      dataIndex: 'billableEvents',
      render: numberFormatter,
    },
    {
      title: '正常访问',
      dataIndex: 'normalVisits',
      render: numberFormatter,
    },
    {
      title: '拦截访问',
      dataIndex: 'interceptedVisits',
      render: (value) => (
        <Tag color={value > 0 ? 'volcano' : 'default'}>{numberFormatter(value)}</Tag>
      ),
    },
    {
      title: '拦截率',
      dataIndex: 'interceptRate',
      render: (value) => percentFormatter(value),
    },
    {
      title: 'IPCore使用',
      dataIndex: 'ipcoreUsage',
      render: (value) => <Tag color="blue">{numberFormatter(value || 0)}</Tag>,
    },
    {
      title: '指纹识别',
      dataIndex: 'fingerprintUsage',
      render: (value) => <Tag color="purple">{numberFormatter(value || 0)}</Tag>,
    },
  ];

  const dailyTotalColumns: ColumnsType<DailyTotalWithKey> = [
    {
      title: '日期',
      dataIndex: 'date',
    },
    {
      title: '总访问量',
      dataIndex: 'totalVisits',
      render: numberFormatter,
    },
    {
      title: '计费事件',
      dataIndex: 'billableEvents',
      render: numberFormatter,
    },
    {
      title: '拦截访问',
      dataIndex: 'interceptedVisits',
      render: numberFormatter,
    },
    {
      title: '拦截率',
      render: (_, record) => {
        const rate = record.totalVisits > 0 ? (record.interceptedVisits / record.totalVisits) * 100 : 0;
        return percentFormatter(rate);
      },
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <Card bodyStyle={{ padding: 24 }}>
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <div>
              <Title level={3} style={{ marginBottom: 8 }}>
                用量管理
              </Title>
              <Paragraph type="secondary" style={{ marginBottom: 0 }}>
                仅系统管理员可见的用量总览。这里集中展示每个客户账号在选定日期范围内的每日访问量、独立IP以及可用于计费的事件指标，为套餐结算和超量提醒提供依据。
              </Paragraph>
            </div>

            <Row gutter={[16, 16]} justify="space-between" align="middle">
              <Col xs={24} md={5}>
                <RangePicker
                  value={dateRange}
                  onChange={handleRangeChange}
                  allowClear={false}
                  format="YYYY-MM-DD"
                  style={{ width: '100%' }}
                />
              </Col>
              <Col xs={24} md={5}>
                <Select
                  mode="multiple"
                  placeholder="按客户筛选（默认全部）"
                  style={{ width: '100%' }}
                  options={clientOptions}
                  value={selectedClients}
                  onChange={setSelectedClients}
                  allowClear
                />
              </Col>
              <Col xs={24} md={3}>
                <Input
                  placeholder="客户ID"
                  value={clientIdInput}
                  onChange={(e) => setClientIdInput(e.target.value)}
                  onPressEnter={() => {
                    const clientId = parseInt(clientIdInput);
                    if (!isNaN(clientId) && clientId > 0) {
                      setSelectedClients([clientId]);
                    } else {
                      setSelectedClients([]);
                    }
                  }}
                  allowClear
                  onClear={() => {
                    setClientIdInput('');
                    setSelectedClients([]);
                  }}
                  style={{ width: '100%' }}
                />
              </Col>
              <Col xs={24} md={2}>
                <Button
                  type="primary"
                  onClick={() => {
                    const clientId = parseInt(clientIdInput);
                    if (!isNaN(clientId) && clientId > 0) {
                      setSelectedClients([clientId]);
                    } else if (clientIdInput === '') {
                      setSelectedClients([]);
                    }
                  }}
                  style={{ width: '100%' }}
                >
                  搜索
                </Button>
              </Col>
              <Col xs={24} md={9}>
                <Space size="large" wrap>
                  <Tooltip title="显示访问量为0的客户数据" placement="bottom">
                    <Space>
                      <Text>包含零访问</Text>
                      <Switch checked={includeEmpty} onChange={setIncludeEmpty} />
                    </Space>
                  </Tooltip>
                  <Button icon={<ReloadOutlined />} onClick={handleRefresh} loading={loading}>
                    刷新数据
                  </Button>
                </Space>
              </Col>
            </Row>

            <Row gutter={[16, 16]}>
              <Col xs={24} sm={12} md={4}>
                <Card bordered={false}>
                  {totals ? (
                    <Statistic title="总访问量" value={totals.totalVisits} formatter={numberFormatter} />
                  ) : (
                    <Skeleton active paragraph={false} />
                  )}
                </Card>
              </Col>
              <Col xs={24} sm={12} md={4}>
                <Card bordered={false}>
                  {totals ? (
                    <Statistic title="计费事件" value={totals.billableEvents} formatter={numberFormatter} />
                  ) : (
                    <Skeleton active paragraph={false} />
                  )}
                </Card>
              </Col>
              <Col xs={24} sm={12} md={4}>
                <Card bordered={false}>
                  {serviceUsage ? (
                    <Statistic 
                      title="IPCore 调用" 
                      value={serviceUsage.summary.ipcore_usage} 
                      formatter={numberFormatter}
                      valueStyle={{ color: '#1890ff' }}
                    />
                  ) : (
                    <Skeleton active paragraph={false} />
                  )}
                </Card>
              </Col>
              <Col xs={24} sm={12} md={4}>
                <Card bordered={false}>
                  {serviceUsage ? (
                    <Statistic 
                      title="指纹识别调用" 
                      value={serviceUsage.summary.fingerprint_usage} 
                      formatter={numberFormatter}
                      valueStyle={{ color: '#722ed1' }}
                    />
                  ) : (
                    <Skeleton active paragraph={false} />
                  )}
                </Card>
              </Col>
              <Col xs={24} sm={12} md={4}>
                <Card bordered={false}>
                  {totals ? (
                    <Statistic
                      title="平均日访问"
                      value={totals.avgPerClientPerDay}
                      precision={2}
                    />
                  ) : (
                    <Skeleton active paragraph={false} />
                  )}
                </Card>
              </Col>
              <Col xs={24} sm={12} md={4}>
                <Card bordered={false}>
                  {totals ? (
                    <Statistic
                      title="平均拦截率"
                      value={totals.avgInterceptRate}
                      precision={2}
                      suffix="%"
                    />
                  ) : (
                    <Skeleton active paragraph={false} />
                  )}
                </Card>
              </Col>
            </Row>

            <Alert
              type="info"
              showIcon
              message={
                <Space direction="vertical" size={2}>
                  <Text strong>计费提示</Text>
                  <Text>
                    计费基准默认参考“计费事件”字段（等同于总访问量）。后续可将套餐包含访问量、超量单价等规则接入，自动生成账单草稿与提醒。
                  </Text>
                </Space>
              }
            />

            {usageData && (
              <Text type="secondary">
                <InfoCircleOutlined style={{ marginRight: 4 }} /> 数据更新时间：
                {usageData.meta.generatedAt ? dayjs(usageData.meta.generatedAt).format('YYYY-MM-DD HH:mm:ss') : '未知'}
              </Text>
            )}
          </Space>
        </Card>

        <Card>
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <Space style={{ width: '100%', justifyContent: 'space-between', alignItems: 'center' }}>
              <Title level={4} style={{ marginBottom: 0 }}>
                数据明细
              </Title>
              <Segmented<ViewMode>
                value={viewMode}
                onChange={(value) => setViewMode(value)}
                options={[
                  { label: '客户汇总', value: 'summary' },
                  { label: '每日明细', value: 'daily' },
                ]}
              />
            </Space>

            {viewMode === 'summary' ? (
              <Table<ClientSummaryWithRank>
                loading={loading}
                columns={summaryColumns}
                dataSource={filteredSummary}
                pagination={{ pageSize: 10, showSizeChanger: false }}
                bordered
                scroll={{ x: 960 }}
              />
            ) : (
              <Table<DailyUsageWithKey>
                loading={loading}
                columns={dailyColumns}
                dataSource={filteredDailyUsage}
                pagination={{ pageSize: 20, showSizeChanger: false }}
                bordered
                scroll={{ x: 960, y: 480 }}
              />
            )}
          </Space>
        </Card>

        <Card title="每日汇总曲线参考">
          <Paragraph type="secondary">
            该表提供选定时间范围内全量访问的日级别聚合，可作为趋势分析和套餐结算的核对依据，后续可衔接折线图或导出功能。
          </Paragraph>
          <Table<DailyTotalWithKey>
            loading={loading}
            columns={dailyTotalColumns}
            dataSource={dailyTotalsWithKeys}
            pagination={{ pageSize: 15, showSizeChanger: false }}
            size="small"
            bordered
          />
        </Card>

        <Card>
          <Title level={4}>后续计费能力规划</Title>
          <Divider style={{ margin: '12px 0' }} />
          <Space direction="vertical" style={{ width: '100%' }}>
            <Text>· 支持为客户配置基础套餐（含量、有效期、超量单价），结合本页数据生成账单草稿与结算记录。</Text>
            <Text>· 引入阈值预警：当计费事件接近套餐上限时，自动通知管理员与客户账号，支持自动升配或临时扩容。</Text>
            <Text>· 打通实际收款流程：记录账单状态（待确认/已出账/已支付），并提供导出与API同步能力。</Text>
            <Text>· 长期规划可结合实时用量推送，与套餐策略联动实现按日或按小时的弹性计费。</Text>
          </Space>
        </Card>
      </Space>
    </div>
  );
};

export default UsageManagement;

